import { type PointerEvent, useCallback, useEffect, useState } from 'react';

import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';

/**
 * Keeps the overlay scrim blocking page hits through the dismissing click, then
 * always clears that block when the gesture ends. `setPointerCapture` plus
 * window `pointerup`/`pointercancel` cover pointerup on the rail, outside the
 * window, cancelled gestures, and capture being denied.
 */
export const useWorkspaceNavOverlayScrimDismiss = () => {
  const hasOpenOverlay = useMainSideNavStore((state) => state.activeOverlay !== null);
  const [isDismissing, setIsDismissing] = useState(false);
  const blocksPagePointerEvents = hasOpenOverlay || isDismissing;

  const startOverlayDismiss = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!hasOpenOverlay) {
        return;
      }

      setIsDismissing(true);
      useMainSideNavStore.getState().setActiveOverlay(null);

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Capture requires an active pointer; pointerup/cancel/lost capture still end the gesture.
      }
    },
    [hasOpenOverlay],
  );

  const endOverlayDismiss = useCallback(() => {
    setIsDismissing(false);
  }, []);

  useEffect(() => {
    if (!isDismissing) {
      return;
    }

    // Pointerup on the rail (z-50, above the scrim) never hits this node unless
    // capture retargets it. Window listeners still end the gesture when capture
    // is denied or the pointer is released on another element.
    window.addEventListener('pointerup', endOverlayDismiss, true);
    window.addEventListener('pointercancel', endOverlayDismiss, true);

    return () => {
      window.removeEventListener('pointerup', endOverlayDismiss, true);
      window.removeEventListener('pointercancel', endOverlayDismiss, true);
    };
  }, [endOverlayDismiss, isDismissing]);

  return {
    blocksPagePointerEvents,
    endOverlayDismiss,
    startOverlayDismiss,
  };
};
